import React, { useEffect, useState } from "react";
import { Checkbox, CircularProgress, Box } from "@mui/material";

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from "@mui/x-data-grid";
import { CreditNoteType } from "pages/detailed-consultation/Models/ECreditNoteType";
import { getCreditNotesAdded, getData, postCreditNoteToAdd, putCreditNotesAdded } from "shared/api/services";
import { GET_ELECTRONIC_DOCS, GET_ELECTRONIC_DOCUMENTS_RELATED } from "shared";
import { IResponseRelatedDocuments } from "shared/models/IResponseRelatedDocuments";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import  "../../styles/_gridCheckCreditNotes.scss";
import { ICreditNoteAdded, IRequestPostCreditNoteAdded } from "pages/detailed-consultation/Models/ICreditNoteAdded";

dayjs.locale("es");

interface CreditNote {
  id: string;
  documentNumber: string;
  issuedAt: string;
  cude: string;
  value: number;
  checked: boolean;
}

interface Props {
  readOnly?: boolean;
  creditNoteType: CreditNoteType;
  selectedDocument: string;
  acceptedValue?: number;
  isFormNoAgreementBalance?: boolean;
}

const GridCheckCreditNotes = ({
  readOnly,
  creditNoteType,
  selectedDocument,
  acceptedValue = 0,
  isFormNoAgreementBalance
}: Props) => {
  const [rows, setRows] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<GridRowId | null>(null);

  const totalValue = React.useMemo(
    () => rows.reduce((sum, row) => sum + (row.value || 0), 0),
    [rows]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const notes = await getData(
          `${GET_ELECTRONIC_DOCS}/${selectedDocument}${GET_ELECTRONIC_DOCUMENTS_RELATED}`
        );
        const notesApply = await getCreditNotesAdded(selectedDocument.toString(), CreditNoteType.Accepted);
        let notesApply2: any[] = [];
        let documentNumbersInArray2 = new Set();

        if(isFormNoAgreementBalance){
          notesApply2 = await getCreditNotesAdded(selectedDocument.toString(), CreditNoteType.NoAgree);
          documentNumbersInArray2 = new Set(notesApply2.map(item => item.legalNumber));
        }

        const documentNumbersInArray1 = new Set(notesApply.map(item => item.legalNumber));

        const result = notes
          .filter(({ applicationStatus, type }: IResponseRelatedDocuments) => applicationStatus?.code === '1' && type.code === '91')
          .map(({ id, documentNumber, uniqueCodeElectronicBilling, issuedAt, totalValue }: IResponseRelatedDocuments) => ({
            id,
            documentNumber,
            cude: uniqueCodeElectronicBilling,
            issuedAt: dayjs(issuedAt).format('DD MMMM YYYY HH:mm'),
            value: totalValue,
            checked: readOnly || (isFormNoAgreementBalance ? documentNumbersInArray2.has(documentNumber) : documentNumbersInArray1.has(documentNumber))
          }));

        const notesToMatch = isFormNoAgreementBalance ? notesApply2 : notesApply;
        const conformedResult = result.filter((note:any) =>
          notesToMatch.some(apply => apply.legalNumber === note.documentNumber)
        );
        const notConformedResult = result.filter((note:any) =>
          !notesApply.some(apply => apply.legalNumber === note.documentNumber)
        );

        readOnly ? setRows(conformedResult) : isFormNoAgreementBalance ? setRows(notConformedResult) : setRows(result);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const addCreditNote = (creditNoteToAdd: CreditNote, id:any) => {
    setLoading(true);

    const request: IRequestPostCreditNoteAdded = {
        value: parseFloat(creditNoteToAdd.value.toString()),
        uniqueCodeElectronicBilling: creditNoteToAdd.cude,
        documentNumber: creditNoteToAdd.documentNumber,
        type: creditNoteType,
        acceptedValue
    }

    postCreditNoteToAdd(request, selectedDocument.toString()).then(()=>{
        setRows((prev) =>
          prev.map((note) =>
            note.id == id ? { ...note, checked: !note.checked } : note
          )
        );
      }).finally(() => { setLoading(false) })
  }

  const deleteCreditNote = (creditNoteToAdd: CreditNote, id:any) => {
      setLoading(true);
      const request: IRequestPostCreditNoteAdded = {
          value: parseFloat(creditNoteToAdd.value.toString()),
          uniqueCodeElectronicBilling: creditNoteToAdd.cude,
          documentNumber: creditNoteToAdd.documentNumber,
          type: creditNoteType,
      }
      putCreditNotesAdded(request, selectedDocument.toString()).then(()=>{
        setRows((prev) =>
          prev.map((note) =>
            note.id == id ? { ...note, checked: !note.checked } : note
          )
        );
      }).finally(() => { setLoading(false) })
  }

  if (readOnly && !loading && rows.length === 0) {
    return null;
  }

  const handleCheck = async (params: GridRenderCellParams) => {
    const { id, row } = params;
    setProcessing(id);
    try {
      if (!row.checked) {
        addCreditNote(params.row, id);
      } else {
        deleteCreditNote(params.row, id);
      }

    } finally {
      setProcessing(null);
    }
  };
  
  const columns: GridColDef[] = [
    {
      field: "checked",
      headerName: "",
      width: 30,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          disabled={processing === params.id || readOnly}
          onChange={() => handleCheck(params)}
        />
      ),
    },
    {
      field: "documentNumber",
      headerName: "Número de nota Crédito",
      width: 200,
    },
    { field: "issuedAt", headerName: "Fecha nota Crédito", width: 180 },
    { field: "cude", headerName: "CUDE", width: 360 },
    {
      field: "value",
      headerName: "Valor",
      width: 120,
      type: "number",
      valueFormatter: ({ value }) =>
        value?.toLocaleString("es-CO", { style: "currency", currency: "COP" }),
    },
  ];

  return (
    <Box className="containerDatagrid">
      {loading ? (
      <CircularProgress />
      ) : (
        <>
        {
          rows.length === 0 ? (
            <Box className="containerNotResults">
              No se encontraron notas crédito.
            </Box>
          ) : (
            <>
              <label
                  className={'richTextLabel'}
              >
                  {isFormNoAgreementBalance ? 'Notas crédito valor no acuerdo aceptado' : 'Notas crédito valor consolidado aceptado'}
              </label>
              <DataGrid
              className={'customDatagrid'}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              hideFooterPagination
              autoHeight
              hideFooterSelectedRowCount
              disableColumnMenu
              hideFooter={false}
              sx={{
                "& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon": {
                  display: "none",
                },
              }}
              slots={{
                footer: () => (
                  <Box className={"footerDatagrid"}>
                  <span className="labelFooter">Valor total: </span>
                  <span> {totalValue.toLocaleString("es-CO", { style: "currency", currency: "COP" })}</span>
                </Box>
                ),
              }}
              />
            </>

          )}
        </>
      )}
    </Box>
  );
};

export default GridCheckCreditNotes;
